/**
 * Created by Abner Sui on 12/04/2019.
 * Description:
 *
 * ------ maintenance history ------
 */

import { Injectable } from '@angular/core';
import { Action } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { switchMap, map, catchError } from 'rxjs/operators';
import { WorkflowService } from '../../services/workflow.service';
import { WorkflowActionTypes, WorkflowAdminVisibilityChange } from '../actions/workflow.actions';
import { FieldDefinition, ProcessDefinition, ProcessInstance, Stage } from '../../models/workflow.model';

@Injectable()
export class WorkflowEffects {

    constructor(
        private _actions$: Actions,
        private _service: WorkflowService,
    ) { }

    adminWorkflowVisibilityEffect$: Observable<any> = createEffect(() => this._actions$.pipe(
        ofType(WorkflowActionTypes.SET_ADMIN_VISIBILITY),
        map((action: any) => action.payload),
        switchMap((visibility: boolean) => {
            return this._service.setAdminVisibility(visibility).pipe(
                map(response => {
                    return new WorkflowAdminVisibilityChange(['workflow', 'enable'], response.enable, true);
                }),
                catchError(err => {
                    if (err) {
                        return of({
                            type: WorkflowActionTypes.ADMIN_VISIBILITY_CHANGE_FAILED,
                            payload: err
                        });
                    }
                }));
        })));

    deleteProcessInstanceEffect$: Observable<Action> = createEffect(() => this._actions$.pipe(
        ofType(WorkflowActionTypes.PROCESS_INSTANCE_DELETE),
        switchMap((action: any) => {
            return this._service.deleteProcessInstanceById(action.payload.instance.id).pipe(
                map(res => {
                    return {
                        type: WorkflowActionTypes.PROCESS_INSTANCE_DELETE_SUCCESS,
                        payload: {
                            instance: action.payload.instance,
                            showNotification: action.payload.showNotification
                        }
                    };
                }),
                catchError(err => {
                    if (err.message.includes('ERR_PROJECT_DELETED')) {
                        return of({
                            type: WorkflowActionTypes.PROCESS_INSTANCE_DELETE_FAILED,
                            payload: {
                                instance: action.payload.instance,
                                showNotification: action.payload.showNotification
                            }
                        });
                    } else {
                        return of({
                            type: WorkflowActionTypes.PROCESS_INSTANCE_DELETE_FAILED,
                            payload: {
                                instance: action.payload.instance,
                                showNotification: action.payload.showNotification
                            }
                        });
                    }
                }),
            );
        })
    ));

    deleteStageEffect$: Observable<Action> = createEffect(() => this._actions$.pipe(
        ofType(WorkflowActionTypes.DELETE_STAGE),
        switchMap((action: any) => {
            return this._service.deleteStageById(action.payload.id).pipe(
                map(res => {
                    return {
                        type: WorkflowActionTypes.DELETE_STAGE_SUCCESS,
                        payload: action.payload,
                    };
                }),
                catchError(err => {
                    return of({
                        type: WorkflowActionTypes.DELETE_STAGE_FAILED,
                    });
                }),
            );
        })
    ));

    getAllFieldsEffect$: Observable<Action> = createEffect(() => this._actions$.pipe(
        ofType(WorkflowActionTypes.GET_ALL_FIELDS),
        switchMap((action: any) => {
            return this._service.getAllWorkflowFieldDefinition().pipe(
                map(res => {
                    const result: Array<FieldDefinition> = [];
                    res['field-definition-list'].forEach(item => {
                        const oneField: FieldDefinition = FieldDefinition.parse(item);
                        if (oneField) {
                            result.push(oneField);
                        }
                    });
                    return {
                        type: WorkflowActionTypes.GET_ALL_FIELDS_SUCCESS,
                        payload: result,
                    };
                }),
                catchError(err => {
                    return of({
                        type: WorkflowActionTypes.GET_ALL_FIELDS_FAILED,
                    });
                }),
            );
        })
    ));

    getIdsEffect$: Observable<Action> = createEffect(() => this._actions$.pipe(
        ofType(WorkflowActionTypes.GET_IDS),
        switchMap((action: any) => {
            return this._service.getIds().pipe(
                map(res => {
                    return {
                        type: WorkflowActionTypes.GET_IDS_SUCCESS,
                        payload: res,
                    };
                }),
                catchError(err => {
                    return of({
                        type: WorkflowActionTypes.GET_IDS_FAILED,
                    });
                }),
            );
        })
    ));

    getOneProcessDefinitionEffect$: Observable<Action> = createEffect(() => this._actions$.pipe(
        ofType(WorkflowActionTypes.GET_ONE_PROCESS_DEFINITION),
        switchMap((action: any) => {
            return this._service.getProcessDefinitionById(action.payload.id).pipe(
                map(res => {
                    if (res) {
                        const one = ProcessDefinition.parse(res);
                        return {
                            type: WorkflowActionTypes.GET_ONE_PROCESS_DEFINITION_SUCCESS,
                            payload: one,
                        };
                    } else {
                        return {
                            type: WorkflowActionTypes.GET_ONE_PROCESS_DEFINITION_FAILED,
                        };
                    }
                }),
                catchError(err => {
                    return of({
                        type: WorkflowActionTypes.GET_ONE_PROCESS_DEFINITION_FAILED,
                    });
                }),
            );
        })
    ));

    updateProcessInstanceEffect$: Observable<Action> = createEffect(() => this._actions$.pipe(
        ofType(WorkflowActionTypes.PROCESS_INSTANCE_ADD_OR_UPDATE),
        switchMap((action: any) => {
            return this._service.getProcessInstanceById(action.payload.id).pipe(
                map(res => {
                    return {
                        type: WorkflowActionTypes.PROCESS_INSTANCE_ADD_OR_UPDATE_SUCCESS,
                        payload: ProcessInstance.parse(res, action.payload.processDefinitionId, action.payload.template),
                    };
                }),
                catchError(err => {
                    return of({
                        type: WorkflowActionTypes.PROCESS_INSTANCE_ADD_OR_UPDATE_FAILED,
                    });
                }),
            );
        })
    ));
}
